import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Server, Plus, Trash2, RefreshCw, CheckCircle, Edit, Copy, Check, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { useCluster } from '../context/ClusterContext';

import { parseUtc } from '../lib/parseUtc';

// The published optops-agent chart version BOTH install commands pin. Keep in
// sync with onboard.sh CHART_VERSION when releasing (was hardcoded 1.2.0 in
// the helm command and unpinned in the script command — a stale hosted
// onboard.sh then installed an old stack; live-caught before the first PoC).
const CHART_VERSION = '1.2.38';

function Clusters() {
  const { refreshClusters } = useCluster();
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCluster, setEditingCluster] = useState(null);
  const [syncing, setSyncing] = useState({});
  const [formData, setFormData] = useState({
    name: ''
  });
  const [newClusterInfo, setNewClusterInfo] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [agentStatus, setAgentStatus] = useState({});
  const [editFormData, setEditFormData] = useState({
    name: '',
    kubeconfig: ''
  });
  // Pro-only install options: when checked, the helm command appends
  // values that enable node optimization at install time. Hidden for
  // free-tier users; superadmin can promote a user to "professional"
  // via /superadmin to unlock this.
  //
  // Node-opt needs cloud-side identifiers the dashboard can't read on
  // its own (cloud cluster name, region, IAM principal). Tunables like
  // spot policy / min savings / consolidation grace flow through the
  // dashboard, not helm, so they are deliberately omitted here.
  const [enableNodeOpt, setEnableNodeOpt] = useState(false);
  const [nodeOptCloud, setNodeOptCloud] = useState('aws');
  const [nodeOptCloudClusterName, setNodeOptCloudClusterName] = useState('');
  const [nodeOptCloudRegion, setNodeOptCloudRegion] = useState('');
  const [nodeOptCloudPrincipal, setNodeOptCloudPrincipal] = useState('');
  // Karpenter provisioner — a sub-choice of node optimization. When the cluster
  // runs Karpenter (or the user wants OptOps to install it), the helm command
  // gets the karpenter.* values and we surface the AWS prerequisite script +
  // step-by-step guidance. 'adopt' = work with an existing Karpenter install;
  // 'install' = bundle Karpenter as a subchart (needs the IAM/SQS prereqs first).
  const [enableKarpenter, setEnableKarpenter] = useState(false);
  const [karpenterAction, setKarpenterAction] = useState('adopt');
  // Container Live Migration — a node-opt sub-toggle. Appends the 3-layer CLM
  // values (node machinery + engine bridge). Per-workload opt-in is a separate
  // kubectl annotate / dashboard step, surfaced in the guidance below.
  const [enableCLM, setEnableCLM] = useState(false);
  // L2 (connection-preserving, same IP) is SCRIPT-ONLY: the one-command
  // script creates the coordinator IAM role and checks same-subnet topology
  // — a raw helm command can do neither. The toggle therefore only feeds
  // buildScriptCommand; the helm tab stays L1 and says so.
  const [enableL2, setEnableL2] = useState(false);
  // CNI posture for the L2 IP handoff — only meaningful when L2 is on, AWS-only.
  // 'jit' (DEFAULT) = node-scoped forked CNI on OptOps L2 nodes only; the
  // customer's cluster-wide aws-node is untouched (smallest blast radius, the
  // script's own default so it emits NO flag). 'full' = cluster-wide node swap:
  // the fork becomes the cluster CNI on every node, emitting the takeover set
  // the script reads (CNI_TAKEOVER=full + L2_CNI_TAKEOVER + CNI_FORCE_VERSION +
  // FORK_CNI_MINOR). SCRIPT-ONLY, like ENABLE_L2 itself.
  const [cniTakeover, setCniTakeover] = useState('jit');
  // Spot auto-rebalance = the disruptor (continuous spot rebalancing + acting on
  // interruption forecasts). ON by default because that IS today's nodeopt
  // behavior (the script's ENABLE_DISRUPTOR defaults true). Leaving it on emits
  // NOTHING (command unchanged); turning it off emits ENABLE_DISRUPTOR=false.
  const [enableSpotRebalance, setEnableSpotRebalance] = useState(true);
  // Predictive spot — INFO-ONLY. spot_forecaster_mode is a per-cluster BACKEND
  // config (node_optimization_config.spot_forecaster_mode), not an onboard/helm
  // env, so this drives an explanatory note only and NEVER touches the command.
  const [predictiveSpot, setPredictiveSpot] = useState('reactive');
  // Install-command view: 'helm' (paste the helm upgrade) | 'script' (one-command
  // curl that also automates cloud IAM). Shared across both modals.
  const [installView, setInstallView] = useState('helm');
  const [currentUserPlan, setCurrentUserPlan] = useState('free');

  // Fetch the current user's plan once so we can gate the node-opt toggle.
  // Endpoint is /auth/profile (there is NO /auth/me — the old call 404'd and
  // silently fell back to "free", hiding the toggles even for pro accounts).
  // The profile response nests the plan type at plan.type.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/auth/profile`);
        if (!cancelled) {
          setCurrentUserPlan(resp.data?.plan?.type || resp.data?.plan?.plan_type || 'free');
        }
      } catch {
        if (!cancelled) setCurrentUserPlan('free');
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // Plan tier no longer gates the install-option toggles (first cluster gets
  // everything on any plan); currentUserPlan is kept for future count-limit UX.
  // eslint-disable-next-line no-unused-vars
  const isPro = currentUserPlan === 'professional' || currentUserPlan === 'enterprise';

  // Single source of truth for the helm install command — both display
  // and copy use this so what's shown is always what's pasted. Previously
  // the display used `api.optops.ai` while the copy callback referenced
  // `dev-api.optops.ai` (legacy bug); now both come from the same function.
  const buildHelmCommand = (info, withNodeOpt, karpenter, clm) => {
    if (!info) return '';
    const apiUrl =
      (typeof window !== 'undefined' && window.location && window.location.hostname.includes('dev'))
        ? 'https://dev-api.optops.ai/api'
        : 'https://api.optops.ai/api';
    const lines = [
      `helm upgrade --install optops-agent oci://public.ecr.aws/m7j3u0d5/charts/optops-agent --version ${CHART_VERSION} \\`,
      `  --namespace optops-system --create-namespace \\`,
      `  --set apiKey="${info.api_key}" \\`,
      `  --set apiUrl="${apiUrl}" \\`,
      `  --set cluster.id="${info.cluster_id || info.id}" \\`,
      `  --set cluster.name="${info.cluster_name || info.name}" \\`,
      `  --set allInOne.enabled=true \\`,
      `  --set allInOne.replicas=1 \\`,
      `  --set allInOne.certificates.useCertManager=false`,
    ];
    if (withNodeOpt) {
      // Node-optimization flags. mode=optimize is THE master switch (default
      // "observe" = read-only); it flips the agent to node-managing AND injects
      // the IRSA/Workload-Identity SA annotation. cloudAuth.* carries the
      // cloud-side identifiers the agent can't read on its own. (Earlier the
      // dashboard emitted cloudProvider.* — a DEAD path the chart ignores, so
      // node-opt silently never engaged; fixed 2026-07-13 to cloudAuth.*.)
      lines[lines.length - 1] = lines[lines.length - 1] + ' \\';
      lines.push(`  --set mode=optimize \\`);
      lines.push(`  --set nodeOptimization.enabled=true \\`);
      lines.push(`  --set cloudAuth.provider="${nodeOptCloud}" \\`);
      if (nodeOptCloud === 'aws') {
        const arn = nodeOptCloudPrincipal || 'arn:aws:iam::<account-id>:role/optops-agent-node-manager';
        lines.push(`  --set cloudAuth.aws.authMethod=irsa \\`);
        lines.push(`  --set cloudAuth.aws.region="${nodeOptCloudRegion || '<your-region>'}" \\`);
        lines.push(`  --set cloudAuth.aws.clusterName="${nodeOptCloudClusterName || info.cluster_name || info.name || '<your-eks-cluster-name>'}" \\`);
        lines.push(`  --set cloudAuth.aws.roleArn="${arn}"`);
      } else {
        const proj = nodeOptCloudPrincipal || '<your-project-id>';
        const sa = `optops-agent@${proj}.iam.gserviceaccount.com`;
        lines.push(`  --set cloudAuth.gcp.authMethod=workloadIdentity \\`);
        lines.push(`  --set cloudAuth.gcp.projectId="${proj}" \\`);
        lines.push(`  --set cloudAuth.gcp.zone="${nodeOptCloudRegion || '<your-zone>'}" \\`);
        lines.push(`  --set cloudAuth.gcp.clusterName="${nodeOptCloudClusterName || info.cluster_name || info.name || '<your-gke-cluster-name>'}" \\`);
        lines.push(`  --set cloudAuth.gcp.serviceAccountEmail="${sa}"`);
      }
      // Karpenter provisioner. The chart gates on karpenter.enabled (RBAC +
      // NodePool/NodeClass templates + agent wiring) and karpenter.controlMode
      // ("optops" = OptOps drives consolidation/disruption collaboratively,
      // guide-not-replace). install stays false (adopt) because the published
      // chart does NOT vendor the Karpenter controller subchart — the controller
      // is either pre-existing (adopt) or installed via the guided steps below
      // (install), and the chart then manages its NodePools either way.
      if (karpenter && karpenter.enabled) {
        lines[lines.length - 1] = lines[lines.length - 1] + ' \\';
        lines.push(`  --set karpenter.enabled=true \\`);
        lines.push(`  --set karpenter.controlMode="optops" \\`);
        lines.push(`  --set karpenter.install=false`);
      }
      // Container Live Migration — L1 (checkpoint/restore, new IP): the safe
      // one-command default. THREE flags are REQUIRED or CLM silently no-ops,
      // because the chart defaults them off/observe:
      //   mode=enforce           — default 'shadow' only OBSERVES, never migrates
      //   imageSwap.enabled=true — default off = every L1 restore fails closed
      //   liveMigration.enabled  — deploys the migrator/coordinator/gate machinery
      // L2 (connection-preserving, same IP) is NOT enabled here: it needs a
      // coordinator IRSA role + region + self-managed VPC CNI that this form
      // doesn't collect — enabling l2 without the role 403s every cutover and
      // downgrades to L1 anyway. For L2, use the one-command script with
      // ENABLE_L2=true (it creates the role). L3 per-workload opt-in is annotate-time.
      if (clm) {
        lines[lines.length - 1] = lines[lines.length - 1] + ' \\';
        lines.push(`  --set liveMigration.enabled=true \\`);
        lines.push(`  --set liveMigration.mode=enforce \\`);
        lines.push(`  --set liveMigration.imageSwap.enabled=true \\`);
        lines.push(`  --set rebalancer.clm.enabled=true \\`);
        lines.push(`  --set rebalancer.clm.consolidationEnabled=true`);
        if (enableL2) {
          lines.push(`# L2 (same-IP, connection-preserving) is not available via raw helm:`);
          lines.push(`# it needs the coordinator IAM role + a same-subnet topology check.`);
          lines.push(`# Use the "One-command script" tab — ENABLE_L2=true does both.`);
        }
      }
    }
    // GKE: the helm path (unlike the one-command script) does NOT create cloud
    // IAM — without these one-time prereqs the install goes green but every
    // node-pool call PERMISSION_DENIEDs (wrong/absent role) or silently uses
    // the node SA (Workload Identity off). Prepended as comments so pasting
    // the whole block stays runnable; kept in sync with onboard.sh's gcp branch.
    if (withNodeOpt && nodeOptCloud !== 'aws') {
      const proj = nodeOptCloudPrincipal || '<your-project-id>';
      const sa = `optops-agent@${proj}.iam.gserviceaccount.com`;
      const prereq = [
        `# GKE one-time prereqs (skip if done): Workload Identity ON + GKE-API role on the GSA.`,
        `# 1) Check WI is enabled (empty output = OFF -> enable it first):`,
        `#      gcloud container clusters describe <cluster> --location <zone> --project ${proj} --format='value(workloadIdentityConfig.workloadPool)'`,
        `# 2) gcloud iam service-accounts create optops-agent --project ${proj}`,
        `# 3) gcloud projects add-iam-policy-binding ${proj} --member serviceAccount:${sa} --role roles/container.clusterAdmin`,
        `# 4) gcloud iam service-accounts add-iam-policy-binding ${sa} --project ${proj} --role roles/iam.workloadIdentityUser \\`,
        `#      --member "serviceAccount:${proj}.svc.id.goog[optops-system/optops-agent]"`,
      ];
      return prereq.join('\n') + '\n' + lines.join('\n');
    }
    return lines.join('\n');
  };

  // One-command install: the hosted onboard.sh does cloud IAM (IRSA / Workload
  // Identity) THEN the helm install, so the customer fills 2-3 blanks instead of
  // creating the IAM role by hand. Env vars are driven by the same toggles as
  // the helm view. withNodeOpt=false => SCOPE=rightsizing (observe, no cloud creds).
  const buildScriptCommand = (info, withNodeOpt, clm) => {
    if (!info) return '';
    const apiUrl =
      (typeof window !== 'undefined' && window.location && window.location.hostname.includes('dev'))
        ? 'https://dev-api.optops.ai/api'
        : 'https://api.optops.ai/api';
    const env = [];
    // Default the cloud cluster name to the OptOps cluster name — overridable
    // via the node-opt cluster-name field when the EKS/GKE name differs.
    const cloudClusterName = nodeOptCloudClusterName || info.cluster_name || info.name || '<your-cluster-name>';
    env.push(`CLOUD=${nodeOptCloud}`);
    // Pin the chart explicitly: the hosted onboard.sh has its own default, but
    // if the hosted copy lags a release, an unpinned command installs an old
    // stack. The dashboard always knows the intended version — say it.
    env.push(`CHART_VERSION=${CHART_VERSION}`);
    env.push(`SCOPE=${withNodeOpt ? 'nodeopt' : 'rightsizing'}`);
    if (withNodeOpt && clm) env.push(`ENABLE_CLM=true`);
    // ENABLE_L2 makes the script create the ENI-handoff coordinator role and
    // run the same-subnet topology check; AWS-only (the script itself refuses
    // it elsewhere with a clear error).
    if (withNodeOpt && clm && enableL2 && nodeOptCloud === 'aws') env.push(`ENABLE_L2=true`);
    // CNI posture for the L2 forked-CNI IP handoff. 'jit' (node-scoped) is the
    // script's default and needs NO flag. 'full' (cluster-wide node swap) emits
    // the takeover set the script reads: CNI_TAKEOVER=full selects the
    // cluster-wide path; L2_CNI_TAKEOVER=true lets it convert an EKS-managed
    // vpc-cni addon to self-managed in place (--preserve); CNI_FORCE_VERSION +
    // FORK_CNI_MINOR accept/pin the fork's VPC-CNI minor for the whole fleet.
    if (withNodeOpt && clm && enableL2 && nodeOptCloud === 'aws' && cniTakeover === 'full') {
      env.push(`CNI_TAKEOVER=full`);
      env.push(`L2_CNI_TAKEOVER=true`);
      // NO forced CNI version: the script auto-detects the cluster's VPC-CNI
      // minor and picks the version-matched fork build. Forcing FORK_CNI_MINOR
      // (was hardcoded 1.20) + CNI_FORCE_VERSION would install a version-skewed
      // CNI on the control plane — the fork was v1.20-only when this was added;
      // it now spans minors, so auto-match is both correct and safe.
    }
    // Spot auto-rebalance (the disruptor) is ON by default for nodeopt, so only
    // emit when the user opts OUT — the default command then stays byte-identical.
    if (withNodeOpt && !enableSpotRebalance) env.push(`ENABLE_DISRUPTOR=false`);
    env.push(`CLUSTER_NAME=${cloudClusterName}`);
    if (withNodeOpt) {
      if (nodeOptCloud === 'aws') {
        env.push(`REGION=${nodeOptCloudRegion || '<your-region>'}`);
      } else {
        env.push(`PROJECT_ID=${nodeOptCloudPrincipal || '<your-project-id>'}`);
        env.push(`ZONE=${nodeOptCloudRegion || '<your-zone>'}`);
      }
    }
    env.push(`API_KEY=${info.api_key}`);
    env.push(`CLUSTER_ID=${info.cluster_id || info.id}`);
    env.push(`BACKEND_URL=${apiUrl}`);
    // one env var per line for readability, backslash-continued into the curl.
    return env.map((e) => `${e} \\`).join('\n') + '\n  bash <(curl -fsSL https://console.optops.ai/onboard.sh)';
  };

  // Shared install-command block (tabbed: Helm | One-command script), used by
  // both the add and edit modals. `info` is the cluster record (api_key etc.).
  const renderInstallCommand = (info, keyPrefix) => {
    // Feature toggles are NOT plan-gated: the first cluster gets everything on
    // any plan (the free tier limits CLUSTER COUNT, enforced at creation by the
    // backend's check_plan_limits), so node-opt/CLM options always render.
    const karpenter = { enabled: enableNodeOpt && enableKarpenter, action: karpenterAction };
    const clm = enableNodeOpt && enableCLM;
    const cmd = installView === 'script'
      ? buildScriptCommand(info, enableNodeOpt, clm)
      : buildHelmCommand(info, enableNodeOpt, karpenter, clm);
    const tabBtn = (v, label) => (
      <button type="button" onClick={() => setInstallView(v)}
        className={`text-xs px-3 py-1 rounded-t border-b-2 ${installView === v
          ? 'border-blue-600 text-blue-700 dark:text-blue-300 font-medium'
          : 'border-transparent text-slate-500 dark:text-text-secondary'}`}>
        {label}
      </button>
    );
    return (
      <div>
        <div className="flex items-center gap-1 mb-1">
          {tabBtn('helm', 'Helm command')}
          {tabBtn('script', 'One-command install')}
        </div>
        {installView === 'script' && (
          <p className="text-xs text-slate-500 dark:text-text-secondary mb-1">
            Automates cloud IAM + install. Fill the <code className="text-[11px]">&lt;...&gt;</code> values, then paste.
          </p>
        )}
        <div className="relative">
          <pre className="p-3 pr-10 bg-slate-100 dark:bg-background-dark dark:text-white rounded border dark:border-surface-border text-xs font-mono whitespace-pre-wrap break-all">
            {cmd}
          </pre>
          <Button size="sm" variant="outline" className="absolute top-2 right-2"
            onClick={() => copyToClipboard(cmd, keyPrefix)}>
            {copiedField === keyPrefix ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    );
  };

  // AWS prerequisites for the "Install Karpenter" path. Karpenter needs cloud
  // infra the helm chart can't create: an IAM controller role, a node instance
  // profile, an SQS interruption queue, and discovery tags. This wraps the
  // official Karpenter CloudFormation (pinned to the version we vendor) so the
  // user runs ONE script, parameterised from the form, before the helm command.
  const buildKarpenterPrereq = () => {
    const name = nodeOptCloudClusterName || '<your-eks-cluster-name>';
    const region = nodeOptCloudRegion || '<your-region>';
    return [
      `# Karpenter AWS prerequisites — run ONCE before the helm command above.`,
      `export CLUSTER_NAME="${name}" AWS_REGION="${region}" KARPENTER_VERSION="1.0.6"`,
      `# 1) IAM controller role + node role + SQS interruption queue (official CFN)`,
      `curl -fsSL "https://raw.githubusercontent.com/aws/karpenter-provider-aws/v\${KARPENTER_VERSION}/website/content/en/preview/getting-started/getting-started-with-karpenter/cloudformation.yaml" > /tmp/karpenter-cf.yaml`,
      `aws cloudformation deploy --stack-name "Karpenter-\${CLUSTER_NAME}" --template-file /tmp/karpenter-cf.yaml \\`,
      `  --capabilities CAPABILITY_NAMED_IAM --parameter-overrides "ClusterName=\${CLUSTER_NAME}" --region "\${AWS_REGION}"`,
      `# 2) Let Karpenter nodes join the cluster (aws-auth identity mapping)`,
      `eksctl create iamidentitymapping --cluster "\${CLUSTER_NAME}" --region "\${AWS_REGION}" \\`,
      `  --arn "arn:aws:iam::$(aws sts get-caller-identity --query Account --output text):role/KarpenterNodeRole-\${CLUSTER_NAME}" \\`,
      `  --group system:bootstrappers --group system:nodes --username "system:node:{{EC2PrivateDNSName}}"`,
      `# 3) Tag your private subnets + node security group for discovery:`,
      `#      karpenter.sh/discovery = \${CLUSTER_NAME}`,
      `# 4) Install the Karpenter controller (official chart). OptOps adopts +`,
      `#    manages its NodePools afterwards via the helm command above.`,
      `helm install karpenter oci://public.ecr.aws/karpenter/karpenter --version "\${KARPENTER_VERSION}" \\`,
      `  --namespace "kube-system" \\`,
      `  --set "settings.clusterName=\${CLUSTER_NAME}" \\`,
      `  --set "settings.interruptionQueue=Karpenter-\${CLUSTER_NAME}" --wait`,
    ].join('\n');
  };

  // Karpenter opt-in sub-section, shared by the new-cluster install modal and the
  // edit-cluster modal (idSuffix keeps DOM ids + copy state unique per modal).
  // Rendered inside the node-optimization block, so it only shows once node opt
  // is enabled (Karpenter is a node-opt provisioner).
  const renderKarpenterOption = (idSuffix) => {
    const copyKey = `karpenter_prereq_${idSuffix}`;
    return (
      <div className="border-t border-zinc-200 dark:border-zinc-700 pt-2 mt-2">
        <div className="flex items-start gap-3">
          <input
            id={`karpenter-enable-${idSuffix}`}
            type="checkbox"
            checked={enableKarpenter}
            onChange={(e) => setEnableKarpenter(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-300 dark:border-zinc-600"
          />
          <label htmlFor={`karpenter-enable-${idSuffix}`} className="text-xs leading-relaxed cursor-pointer">
            <div className="font-medium text-zinc-900 dark:text-white">This cluster uses Karpenter</div>
            <div className="text-zinc-500 dark:text-zinc-400 mt-0.5">
              OptOps drives Karpenter NodePools collaboratively (guide-not-replace, never
              destructive). Adopt an existing install, or let OptOps install it.
            </div>
          </label>
        </div>
        {enableKarpenter && (
          <div className="mt-2 pl-7 space-y-2">
            <div className="flex gap-2">
              <button type="button" onClick={() => setKarpenterAction('adopt')}
                className={`text-xs px-3 py-1 rounded border ${karpenterAction === 'adopt' ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}>
                Adopt existing
              </button>
              <button type="button" onClick={() => setKarpenterAction('install')}
                className={`text-xs px-3 py-1 rounded border ${karpenterAction === 'install' ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}>
                Install Karpenter
              </button>
            </div>
            {karpenterAction === 'adopt' && (
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                OptOps reads your existing Karpenter NodePools and proposes consolidation /
                disruption — it never deletes NodePools or forces drains on its own.
              </p>
            )}
            {karpenterAction === 'install' && nodeOptCloud === 'aws' && (
              <div className="rounded border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-2 space-y-1.5">
                <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                  Install Karpenter first (one-time setup)
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300/80">
                  Karpenter needs AWS prerequisites (IAM controller role, node instance profile,
                  SQS interruption queue) plus the controller itself. This script — official
                  CloudFormation + the Karpenter chart — sets it all up; then the helm command
                  above has OptOps adopt and manage its NodePools:
                </p>
                <div className="relative">
                  <pre className="p-2 pr-9 bg-white/70 dark:bg-background-dark dark:text-white rounded border border-amber-200 dark:border-surface-border text-[11px] font-mono whitespace-pre-wrap break-all">
                    {buildKarpenterPrereq()}
                  </pre>
                  <Button size="sm" variant="outline" className="absolute top-1.5 right-1.5"
                    onClick={() => copyToClipboard(buildKarpenterPrereq(), copyKey)}>
                    {copiedField === copyKey ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            )}
            {karpenterAction === 'install' && nodeOptCloud === 'gcp' && (
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                On GKE, OptOps installs the community GCP Karpenter provider (GCENodeClass).
                No SQS/IAM stack — the Workload Identity binding above is the only prerequisite.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Container Live Migration opt-in — a node-opt sub-toggle, shared by both
  // modals (idSuffix keeps DOM ids unique).
  const renderCLMOption = (idSuffix) => {
    return (
      <div className="rounded border border-zinc-200 dark:border-zinc-700 p-3">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id={`clm-enable-${idSuffix}`}
            checked={enableCLM}
            onChange={(e) => setEnableCLM(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-300 dark:border-zinc-600"
          />
          <label htmlFor={`clm-enable-${idSuffix}`} className="text-xs leading-relaxed cursor-pointer">
            <div className="font-medium text-zinc-900 dark:text-white">Live migration (state-preserving moves)</div>
            <div className="text-zinc-500 dark:text-zinc-400 mt-0.5">
              Move workloads across nodes without restarting — process state preserved
              (L1: the pod resumes with a new IP). Best-effort with clean-eviction fallback;
              availability always safe.
            </div>
          </label>
        </div>
        {enableCLM && (
          <div className="mt-2 pl-7 flex items-start gap-2">
            <input
              type="checkbox"
              id={`clm-l2-enable-${idSuffix}`}
              checked={enableL2}
              onChange={(e) => { setEnableL2(e.target.checked); if (e.target.checked) setInstallView('script'); }}
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 dark:border-zinc-600"
            />
            <label htmlFor={`clm-l2-enable-${idSuffix}`} className="text-xs leading-relaxed cursor-pointer">
              <div className="font-medium text-zinc-900 dark:text-white">L2 — connection-preserving (same IP, open TCP survives)</div>
              <div className="text-zinc-500 dark:text-zinc-400 mt-0.5">
                AWS only, one-command script only: the script creates the ENI-handoff IAM role
                and verifies a same-subnet node pair exists (L2&apos;s hard requirement — without
                one it downgrades to L1). Selecting this switches to the script tab and adds{' '}
                <code className="text-[11px]">ENABLE_L2=true</code>.
              </div>
            </label>
          </div>
        )}
        {enableCLM && enableL2 && nodeOptCloud === 'aws' && (
          <div className="mt-2 pl-7">
            <div className="text-xs font-medium text-zinc-900 dark:text-white mb-1">
              CNI posture for the L2 IP handoff
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setCniTakeover('jit')}
                className={`text-xs px-3 py-1 rounded border ${cniTakeover === 'jit' ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}>
                Node-scoped (JIT)
              </button>
              <button type="button" onClick={() => setCniTakeover('full')}
                className={`text-xs px-3 py-1 rounded border ${cniTakeover === 'full' ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}>
                Full cluster-wide node swap
              </button>
            </div>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              {cniTakeover === 'jit'
                ? 'The forked CNI runs only on OptOps L2 nodes; your cluster-wide aws-node is untouched. Smallest blast radius (recommended).'
                : 'The forked CNI becomes the cluster CNI on every node (safe cni-migrate controller: canary + per-node health gate + auto-rollback). Adds CNI_TAKEOVER=full, L2_CNI_TAKEOVER=true, CNI_FORCE_VERSION=true, FORK_CNI_MINOR=1.20 to the script.'}
            </p>
          </div>
        )}
        {enableCLM && (
          <p className="mt-2 pl-7 text-xs text-zinc-500 dark:text-zinc-400">
            After install, opt workloads in from Node Optimization → Live Migration (per-workload
            toggles), or annotate the <em>pod template</em>:{' '}
            <code className="text-[11px]">{'kubectl -n <ns> patch deploy <name> -p \'{"spec":{"template":{"metadata":{"annotations":{"optops.io/live-migrate":"true"}}}}}\''}</code>
            {' '}(one rolling restart; pinned <code className="text-[11px]">do-not-disrupt</code> pods are always migrated).
          </p>
        )}
      </div>
    );
  };

  // Spot posture opt-in — a node-opt sub-section, shared by both modals.
  //  • Spot auto-rebalance maps to the REAL onboard env ENABLE_DISRUPTOR (ON by
  //    default; off → ENABLE_DISRUPTOR=false in the script command).
  //  • Predictive spot is INFO-ONLY: spot_forecaster_mode lives in the per-cluster
  //    backend config (node_optimization_config.spot_forecaster_mode), NOT in the
  //    onboard script or helm chart, so we describe the real mechanism instead of
  //    emitting a fake flag.
  const renderSpotOption = (idSuffix) => {
    return (
      <div className="rounded border border-zinc-200 dark:border-zinc-700 p-3 space-y-2">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id={`spot-rebalance-${idSuffix}`}
            checked={enableSpotRebalance}
            onChange={(e) => setEnableSpotRebalance(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-300 dark:border-zinc-600"
          />
          <label htmlFor={`spot-rebalance-${idSuffix}`} className="text-xs leading-relaxed cursor-pointer">
            <div className="font-medium text-zinc-900 dark:text-white">Spot auto-rebalance</div>
            <div className="text-zinc-500 dark:text-zinc-400 mt-0.5">
              Continuously rebalance spot capacity and act on interruption forecasts (the
              disruptor). On by default; turning it off adds{' '}
              <code className="text-[11px]">ENABLE_DISRUPTOR=false</code> to the one-command script.
            </div>
          </label>
        </div>
        <div className="border-t border-zinc-200 dark:border-zinc-700 pt-2">
          <div className="text-xs font-medium text-zinc-900 dark:text-white mb-1">Predictive spot</div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setPredictiveSpot('reactive')}
              className={`text-xs px-3 py-1 rounded border ${predictiveSpot === 'reactive' ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}>
              Off (reactive)
            </button>
            <button type="button" onClick={() => setPredictiveSpot('shadow')}
              className={`text-xs px-3 py-1 rounded border ${predictiveSpot === 'shadow' ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}>
              Shadow (observe-only)
            </button>
            <button type="button" onClick={() => setPredictiveSpot('predictive')}
              className={`text-xs px-3 py-1 rounded border ${predictiveSpot === 'predictive' ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}>
              On (predictive)
            </button>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
            {predictiveSpot === 'reactive'
              ? 'Reactive rescue is always on: real interruption notices and rebalance recommendations trigger live-migration rescue. Predictive adds acting BEFORE those signals.'
              : 'This is a per-cluster setting, not an install flag — after the agent connects, set it from Node Optimization → Spot Reliability (it stores spot_forecaster_mode in your cluster config; the install command is unchanged).'}
          </p>
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchClusters();
    checkAgentStatus(); // Check immediately on mount
    const interval = setInterval(checkAgentStatus, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      setNewClusterInfo(null);
      setCopiedField(null);
      setFormData({ name: '' });
    }
  };

  const openAddClusterDialogForTour = useCallback(() => {
    setNewClusterInfo(null);
    setCopiedField(null);
    setFormData({ name: '' });
    setDialogOpen(true);
  }, []);

  useEffect(() => {
    const handleOpenForTour = () => openAddClusterDialogForTour();

    window.addEventListener('optops-tour-open-add-cluster-dialog', handleOpenForTour);

    return () => {
      window.removeEventListener('optops-tour-open-add-cluster-dialog', handleOpenForTour);
    };
  }, [openAddClusterDialogForTour]);

  const checkAgentStatus = async () => {
    try {
      const response = await axios.get(`${API}/agent/status`);
      const statusMap = {};
      response.data.agent_status.forEach(status => {
        statusMap[status.cluster_id] = status.agent_status === 'connected';
      });
      setAgentStatus(statusMap);
    } catch (error) {
      console.error('Failed to check agent status:', error);
    }
  };

  const fetchClusters = async () => {
    try {
      const response = await axios.get(`${API}/clusters`);
      setClusters(response.data);
      // Keep the global context in sync so the header dropdown reflects
      // any creates/deletes without a page refresh.
      refreshClusters();
    } catch (error) {
      toast.error('Failed to load clusters');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCluster = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post(`${API}/clusters`, {
        name: formData.name,
        connection_mode: 'agent'
      });

      console.log('Cluster creation response:', response.data);

      setNewClusterInfo({
        cluster_id: response.data.id,
        api_key: response.data.api_key || 'API key not returned',
        cluster_name: response.data.name
      });
      window.dispatchEvent(new CustomEvent('optops-tour-cluster-created'));

      toast.success('Cluster created! Configure your agent with the details below.');
      setFormData({ name: '' });
      fetchClusters();
      checkAgentStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add cluster');
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setNewClusterInfo(null);
  };

  const handleDeleteCluster = async (clusterId) => {
    if (!window.confirm('Are you sure you want to delete this cluster?')) return;

    try {
      await axios.delete(`${API}/clusters/${clusterId}`);
      toast.success('Cluster deleted');
      fetchClusters();
    } catch (error) {
      toast.error('Failed to delete cluster');
    }
  };

  const handleSyncMetrics = async (clusterId) => {
    setSyncing({ ...syncing, [clusterId]: true });

    try {
      await axios.post(`${API}/metrics/sync/${clusterId}`);
      toast.success('Metrics synced successfully!');
      fetchClusters();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to sync metrics. Please check your kubeconfig and cluster connectivity.';
      toast.error(errorMsg);
    } finally {
      setSyncing({ ...syncing, [clusterId]: false });
    }
  };

  const handleEditCluster = async (cluster) => {
    // Fetch cluster API key from profile
    try {
      const response = await axios.get(`${API}/auth/profile`);
      const clusterApiKey = response.data.api_keys.find(key => key.cluster_id === cluster.id);

      setEditingCluster({
        ...cluster,
        api_key: clusterApiKey?.key || 'Not available'
      });
      setEditFormData({
        name: cluster.name
      });
      setEditDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load cluster details');
    }
  };

  const handleUpdateCluster = async (e) => {
    e.preventDefault();

    try {
      await axios.put(`${API}/clusters/${editingCluster.id}`, {
        name: editFormData.name,
        connection_mode: 'agent'
      });

      toast.success('Cluster name updated successfully!');
      setEditDialogOpen(false);
      setEditingCluster(null);
      setEditFormData({ name: '' });
      fetchClusters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cluster');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="skeleton h-8 w-48 rounded"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-48 rounded-xl"></div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 via-white to-cyan-50 dark:from-surface-dark dark:via-surface-dark dark:to-surface-dark dark:border dark:border-surface-border">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">Kubernetes Clusters</h1>
                <p className="text-slate-600 dark:text-text-secondary mt-2 text-lg flex items-center">
                  <Server className="w-5 h-5 mr-2 text-blue-500" />
                  Manage and monitor your connected clusters
                </p>
              </div>

              <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 shadow-lg hover-lift transition-smooth"
                    data-testid="add-cluster-button"
                    id="tour-add-cluster-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Cluster
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Cluster</DialogTitle>
                    <DialogDescription>
                      Create a cluster and get credentials for agent deployment
                    </DialogDescription>
                  </DialogHeader>

                  {!newClusterInfo ? (
                    <form onSubmit={handleAddCluster} className="space-y-4" data-testid="add-cluster-form">
                      <div className="space-y-2">
                        <Label htmlFor="cluster-name">Cluster Name</Label>
                        <Input
                          id="tour-cluster-name-input"
                          data-testid="cluster-name-input"
                          placeholder="minikube-local"
                          value={formData.name}
                          autoFocus
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>

                      <div className="flex justify-end gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleDialogOpenChange(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-blue-600 hover:bg-blue-700" id="tour-submit-cluster-btn" data-testid="submit-cluster-button">
                          Create Cluster
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 rounded-lg">
                        <p className="text-sm text-green-800 dark:text-green-300 font-medium mb-2">✓ Cluster created successfully!</p>
                        <p className="text-xs text-green-700 dark:text-green-400">Use the credentials below to deploy the OptOps agent in your cluster.</p>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-slate-500 dark:text-text-secondary">Cluster ID</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <code
                              id="tour-cluster-id"
                              className="flex-1 p-2 bg-slate-100 dark:bg-background-dark dark:text-white rounded border dark:border-surface-border text-sm font-mono"
                            >
                              {newClusterInfo.cluster_id}
                            </code>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyToClipboard(newClusterInfo.cluster_id, 'cluster_id')}
                            >
                              {copiedField === 'cluster_id' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs text-slate-500 dark:text-text-secondary">API Key</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <code
                              id="tour-api-key"
                              className="flex-1 p-2 bg-slate-100 dark:bg-background-dark dark:text-white rounded border dark:border-surface-border text-sm font-mono break-all"
                            >
                              {newClusterInfo.api_key}
                            </code>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyToClipboard(newClusterInfo.api_key, 'api_key')}
                            >
                              {copiedField === 'api_key' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {(
                        <div className="rounded border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/50">
                          <div className="flex items-start gap-3 p-3">
                            <input
                              id="nodeopt-enable-new"
                              type="checkbox"
                              checked={enableNodeOpt}
                              onChange={(e) => setEnableNodeOpt(e.target.checked)}
                              className="mt-1 h-4 w-4 rounded border-zinc-300 dark:border-zinc-600"
                            />
                            <label htmlFor="nodeopt-enable-new" className="text-xs leading-relaxed cursor-pointer">
                              <div className="font-medium text-zinc-900 dark:text-white">
                                Enable node optimization at install
                              </div>
                              <div className="text-zinc-500 dark:text-zinc-400 mt-0.5">
                                Adds cloud-provider config and the IAM service-account annotation
                                so the agent can read pool state and run replace/consolidate actions.
                                Spot policy, min savings, and other tunables stay in the dashboard.
                              </div>
                            </label>
                          </div>
                          {enableNodeOpt && (
                            <div className="border-t border-zinc-200 dark:border-zinc-700 p-3 space-y-2">
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => setNodeOptCloud('aws')}
                                  className={`text-xs px-3 py-1 rounded border ${nodeOptCloud === 'aws' ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}
                                >
                                  AWS (EKS)
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setNodeOptCloud('gcp')}
                                  className={`text-xs px-3 py-1 rounded border ${nodeOptCloud === 'gcp' ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}
                                >
                                  GCP (GKE)
                                </button>
                              </div>
                              <div className="grid grid-cols-1 gap-2">
                                <Input
                                  placeholder={nodeOptCloud === 'aws' ? 'EKS cluster name (e.g. prod-eks-us-east-1)' : 'GKE cluster name (e.g. prod-gke-us-central1)'}
                                  value={nodeOptCloudClusterName}
                                  onChange={(e) => setNodeOptCloudClusterName(e.target.value)}
                                  className="text-xs"
                                />
                                <Input
                                  placeholder={nodeOptCloud === 'aws' ? 'AWS region (e.g. us-east-1)' : 'GCP region (e.g. us-central1)'}
                                  value={nodeOptCloudRegion}
                                  onChange={(e) => setNodeOptCloudRegion(e.target.value)}
                                  className="text-xs"
                                />
                                <Input
                                  placeholder={nodeOptCloud === 'aws' ? 'IAM role ARN (from install-aws-irsa.md)' : 'GCP project ID (SA email is built from this)'}
                                  value={nodeOptCloudPrincipal}
                                  onChange={(e) => setNodeOptCloudPrincipal(e.target.value)}
                                  className="text-xs"
                                />
                              </div>
                              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                                Run the {nodeOptCloud === 'aws' ? 'IRSA' : 'Workload Identity'} bootstrap once before
                                installing the agent — see the docs.
                              </p>
                              {renderKarpenterOption('new')}
                              {renderCLMOption('new')}
                              {renderSpotOption('new')}
                            </div>
                          )}
                        </div>
                      )}

                      <div id="tour-install-command">
                        <Label className="text-xs text-slate-500 dark:text-text-secondary">Install Agent Command</Label>
                        <div className="mt-1">
                          {renderInstallCommand(newClusterInfo, 'install_cmd_new')}
                        </div>
                      </div>

                      <div className="p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg">
                        <p className="text-xs text-blue-800 dark:text-blue-300">
                          Run the above command in your terminal. The cluster status will turn green once the agent connects.
                        </p>
                      </div>

                      <div className="flex justify-end">
                        <Button onClick={closeDialog} className="bg-blue-600 hover:bg-blue-700">
                          Done
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Edit Cluster Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Cluster Details</DialogTitle>
              <DialogDescription>
                View and update cluster information
              </DialogDescription>
            </DialogHeader>

            {editingCluster && (
              <div className="space-y-4">
                <form onSubmit={handleUpdateCluster} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-cluster-name">Cluster Name</Label>
                    <Input
                      id="edit-cluster-name"
                      placeholder="Production Cluster"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-3 pt-4 border-t dark:border-surface-border">
                    <div>
                      <Label className="text-xs text-slate-500 dark:text-text-secondary">Cluster ID</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 p-2 bg-slate-100 dark:bg-background-dark dark:text-white rounded border dark:border-surface-border text-sm font-mono">
                          {editingCluster.id}
                        </code>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(editingCluster.id, 'edit_cluster_id')}
                        >
                          {copiedField === 'edit_cluster_id' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs text-slate-500 dark:text-text-secondary">API Key</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 p-2 bg-slate-100 dark:bg-background-dark dark:text-white rounded border dark:border-surface-border text-sm font-mono break-all">
                          {editingCluster.api_key}
                        </code>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(editingCluster.api_key, 'edit_api_key')}
                        >
                          {copiedField === 'edit_api_key' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>

                    {(
                      <div className="rounded border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/50">
                        <div className="flex items-start gap-3 p-3">
                          <input
                            id="nodeopt-enable-edit"
                            type="checkbox"
                            checked={enableNodeOpt}
                            onChange={(e) => setEnableNodeOpt(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-zinc-300 dark:border-zinc-600"
                          />
                          <label htmlFor="nodeopt-enable-edit" className="text-xs leading-relaxed cursor-pointer">
                            <div className="font-medium text-zinc-900 dark:text-white">
                              Enable node optimization
                            </div>
                            <div className="text-zinc-500 dark:text-zinc-400 mt-0.5">
                              Re-run the helm command with cloud-provider config and the IAM annotation.
                              Tunables stay in the dashboard.
                            </div>
                          </label>
                        </div>
                        {enableNodeOpt && (
                          <div className="border-t border-zinc-200 dark:border-zinc-700 p-3 space-y-2">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setNodeOptCloud('aws')}
                                className={`text-xs px-3 py-1 rounded border ${nodeOptCloud === 'aws' ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}
                              >
                                AWS (EKS)
                              </button>
                              <button
                                type="button"
                                onClick={() => setNodeOptCloud('gcp')}
                                className={`text-xs px-3 py-1 rounded border ${nodeOptCloud === 'gcp' ? 'bg-zinc-900 text-white border-zinc-900 dark:bg-white dark:text-zinc-900 dark:border-white' : 'bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600'}`}
                              >
                                GCP (GKE)
                              </button>
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              <Input
                                placeholder={nodeOptCloud === 'aws' ? 'EKS cluster name' : 'GKE cluster name'}
                                value={nodeOptCloudClusterName}
                                onChange={(e) => setNodeOptCloudClusterName(e.target.value)}
                                className="text-xs"
                              />
                              <Input
                                placeholder={nodeOptCloud === 'aws' ? 'AWS region' : 'GCP region'}
                                value={nodeOptCloudRegion}
                                onChange={(e) => setNodeOptCloudRegion(e.target.value)}
                                className="text-xs"
                              />
                              <Input
                                placeholder={nodeOptCloud === 'aws' ? 'IAM role ARN' : 'GCP project ID'}
                                value={nodeOptCloudPrincipal}
                                onChange={(e) => setNodeOptCloudPrincipal(e.target.value)}
                                className="text-xs"
                              />
                            </div>
                            {renderKarpenterOption('edit')}
                            {renderCLMOption('edit')}
                            {renderSpotOption('edit')}
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <Label className="text-xs text-slate-500 dark:text-text-secondary">Install Agent Command</Label>
                      <div className="mt-1">
                        {renderInstallCommand(editingCluster, 'install_cmd_edit')}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      Update Name
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Clusters Grid */}
        {clusters.length === 0 ? (
          <Card className="dark:bg-surface-dark dark:border-surface-border" id="tour-clusters-empty-state">
            <CardContent className="py-16">
              <div className="text-center">
                <Server className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">No Clusters Connected</h3>
                <p className="text-slate-600 dark:text-text-secondary mb-6">Get started by connecting your first Kubernetes cluster</p>
                <Button
                  onClick={() => setDialogOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Cluster
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-fade-in" id="tour-clusters-grid">
            {clusters.map((cluster) => (
              <Card key={cluster.id} className="border-0 shadow-lg hover-lift transition-smooth dark:bg-surface-dark dark:border dark:border-surface-border" data-testid={`cluster-card-${cluster.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl shadow-lg">
                        <Server className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-bold dark:text-white">{cluster.name}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Circle
                            className={`w-2 h-2 ${agentStatus[cluster.id]
                              ? 'fill-green-500 text-green-500 animate-pulse'
                              : 'fill-gray-300 text-gray-300'
                              }`}
                          />
                          <span className={`text-xs font-medium ${agentStatus[cluster.id] ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-text-secondary'
                            }`}>
                            {agentStatus[cluster.id] ? 'Connected' : 'Disconnected'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 text-sm bg-slate-50 dark:bg-background-dark rounded-lg p-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-text-secondary">Created:</span>
                      <span className="text-slate-900 dark:text-white font-medium">
                        {parseUtc(cluster.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-text-secondary">Last Sync:</span>
                      <span className="text-slate-900 dark:text-white font-medium">
                        {cluster.last_sync
                          ? parseUtc(cluster.last_sync).toLocaleString()
                          : 'Never'}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 transition-all"
                      onClick={() => handleSyncMetrics(cluster.id)}
                      disabled={syncing[cluster.id]}
                      data-testid={`sync-button-${cluster.id}`}
                    >
                      {syncing[cluster.id] ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Sync
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 transition-all"
                      onClick={() => handleEditCluster(cluster)}
                      data-testid={`edit-button-${cluster.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300 transition-all"
                      onClick={() => handleDeleteCluster(cluster.id)}
                      data-testid={`delete-button-${cluster.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Clusters;
